import { ConfigurationSettings } from '/models/configuration-settings';
import { clients } from 'lib/https';

export interface WhiteLabelSettings {
  logoUrl: string;
  loginBannerUrl: string;
  logoUrlDarkMode: string;
  loginBannerUrlDarkMode: string;
  themeCustomColor: string;
}

export interface GetWhiteLabelSettingsResponse {
  data: WhiteLabelSettings;
  errors: unknown;
}

/**
 * Fetches white-label settings for the application.
 * This is a mock implementation that returns predefined white-label settings
 * with a simulated API delay.
 *
 * @param {ConfigurationSettings} config - The configuration settings containing API paths and blocksKey.
 * @returns {Promise<GetWhiteLabelSettingsResponse>} The response containing white-label settings.
 *
 * @example
 * const settings = await getWhiteLabelSettings(config);
 */
export const getWhiteLabelSettings = async (
  config: ConfigurationSettings
): Promise<WhiteLabelSettings> => {
  const params = new URLSearchParams({
    ProjectKey: config.blocksKey,
  });
  const baseUrl = `https://${config.paths.baseUrl}`;
  const url = `${baseUrl}${config.paths.bebrave.getWhiteLabelConfiguation}?${params.toString()}`;
  return clients.get<WhiteLabelSettings>(url, config.blocksKey);
};

/**
 * Initializes white-label settings and updates the store
 * This should be called once during application startup to load white-label configuration
 */
export const initializeWhiteLabelSettings = async (
  config: ConfigurationSettings,
  setWhiteLabelSettings: (settings: WhiteLabelSettings | null) => void,
  setLoading: (loading: boolean) => void,
  setError: (error: string | null) => void
): Promise<void> => {
  try {
    setLoading(true);
    setError(null);
    const response = await getWhiteLabelSettings(config);
    setWhiteLabelSettings(response);
  } catch (error) {
    setError(error instanceof Error ? error.message : 'Failed to fetch white-label settings');
    setWhiteLabelSettings(null);
  } finally {
    setLoading(false);
  }
};
